import { useState, useEffect, useRef } from "react";
import "./Autocomplete.css";
import { getDefaultUserAvatar, iconPath } from "winslow-core";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";
import TextInput from "./TextInput";

type Option = {
  value: string;
  label: string;
  imageUrl?: string;
  color?: string;
};

type AutocompleteProps = {
  options: Option[][];
  sectionLabels: string[];
  onChange: (value: string) => void;
  roundIcons?: boolean;
  placeholder?: string;
  openOnTop?: boolean;
  disabled?: boolean;
  selectedValue?: string;
  preventOpen?: boolean;
  onPreventedOpen?: () => void;
  value?: string;
  allowEmpty?: boolean;
  showClearButton?: boolean;
};

export const Autocomplete = ({
  options,
  sectionLabels,
  onChange,
  roundIcons = false,
  placeholder = "(no filter)",
  openOnTop = false,
  disabled = false,
  selectedValue: initialValue,
  preventOpen = false,
  onPreventedOpen,
  value,
  allowEmpty = false,
  showClearButton = false,
}: AutocompleteProps) => {
  const storeUser = useSelector(selectUser);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    value || initialValue
  );
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.map((sectionOptions) =>
    sectionOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const findOptionInSections = (searchValue: string) => {
    for (const sectionOptions of options) {
      const found = sectionOptions.find(
        (option) => option.value === searchValue
      );
      if (found) return found;
    }
    return undefined;
  };

  const handleSelect = (value: string, label: string) => {
    setSelectedValue(value);
    setSearchTerm(label);
    setIsOpen(false);
    onChange(value);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (initialValue !== undefined) {
      const selectedOption = findOptionInSections(initialValue);
      if (selectedOption) {
        setSelectedValue(initialValue);
        setSearchTerm(selectedOption.label);
      }
    }
  }, [initialValue, options]);

  useEffect(() => {
    if (value !== undefined) {
      const selectedOption = findOptionInSections(value);
      if (selectedOption) {
        setSelectedValue(value);
        setSearchTerm(selectedOption.label);
      }
    }
  }, [value, options]);

  const handleInputFocus = () => {
    if (disabled || preventOpen) {
      if (preventOpen && onPreventedOpen) {
        onPreventedOpen();
      }
      return;
    }
    setIsOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (allowEmpty && newValue === "") {
      setSelectedValue(undefined);
      onChange("");
    } else {
      setIsOpen(true);
    }
  };

  const handleInputClick = () => {
    if (allowEmpty && document.activeElement === inputRef.current) {
      setSearchTerm("");
      setSelectedValue(undefined);
      onChange("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchTerm("");
    setSelectedValue(undefined);
    onChange("");
    setIsOpen(false);
  };

  return (
    <div
      ref={autocompleteRef}
      className={`autocomplete-container ${disabled ? "autocomplete-disabled" : ""}`}>
      <div className="autocomplete-selected">
        <TextInput
          inputRef={inputRef}
          title="Search"
          value={searchTerm}
          setValue={(value: string) => {
            handleInputChange({
              target: { value },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          handleSubmit={() => {
            if (filteredOptions[0]?.[0]) {
              handleSelect(
                filteredOptions[0][0].value,
                filteredOptions[0][0].label
              );
            }
          }}
        />
        {showClearButton && searchTerm && (
          <div className="autocomplete-clear" onClick={handleClear}>
            <img src={iconPath("close.svg", storeUser)} alt="Clear" />
          </div>
        )}
        <div className="autocomplete-arrow">
          <img src={iconPath("down.svg", storeUser)} alt="" />
        </div>
      </div>
      {isOpen &&
        !disabled &&
        !preventOpen &&
        filteredOptions.some((section) => section.length > 0) && (
          <div
            className={`autocomplete-dropdown ${
              openOnTop
                ? "autocomplete-dropdown-top"
                : "autocomplete-dropdown-bottom"
            }`}>
            {filteredOptions.map(
              (sectionOptions, sectionIndex) =>
                sectionOptions.length > 0 && (
                  <div key={sectionIndex} className="autocomplete-section">
                    <div className="autocomplete-section-label">
                      {sectionLabels[sectionIndex]}
                    </div>
                    {sectionOptions.map((option) => (
                      <div
                        key={option.value}
                        className="autocomplete-option"
                        onClick={() =>
                          handleSelect(option.value, option.label)
                        }>
                        {option.imageUrl ? (
                          <img
                            src={option.imageUrl}
                            alt={getDefaultUserAvatar(option.label)}
                            className={
                              roundIcons
                                ? "autocomplete-avatar"
                                : "autocomplete-icon"
                            }
                          />
                        ) : option.color ? (
                          <SvgCircle color={option.color} size={40} />
                        ) : null}
                        <div className="autocomplete-label">{option.label}</div>
                      </div>
                    ))}
                  </div>
                )
            )}
          </div>
        )}
    </div>
  );
};

type CircleProps = {
  color: string;
  size?: number;
};

const SvgCircle: React.FC<CircleProps> = ({ color, size = 100 }) => {
  const radius = size / 2 - 5;

  return (
    <svg
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Colored circle">
      <circle r={radius} cx={size / 2} cy={size / 2} fill={color} />
    </svg>
  );
};

export default Autocomplete;
